import { SchoolAutocomplete } from "@/components/SchoolAutocomplete";
import { useState, useEffect } from "react";
import { useParams, useLocation } from "wouter";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { CheckCircle, Loader2 } from "lucide-react";
import { getDisplayName } from "@/lib/userDisplay";
import { useQuery } from "@tanstack/react-query";

export default function Onboarding() {
  const { token } = useParams<{ token: string }>();
  const [, setLocation] = useLocation();
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [validating, setValidating] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [tokenValid, setTokenValid] = useState(false);
  const [email, setEmail] = useState("");

  // Fetch grades from API
  const { data: grades = [] } = useQuery<Array<{id: string; gradeNumber: number; displayName: string; isActive: boolean; sortOrder: number}>>({
    queryKey: ['/api/curriculum/grades'],
  });

  const activeGrades = grades
    .filter(g => g.isActive)
    .sort((a, b) => a.sortOrder - b.sortOrder);

  const comingSoonGrades = grades
    .filter(g => !g.isActive && [7, 8, 9, 10].includes(g.gradeNumber))
    .sort((a, b) => a.gradeNumber - b.gradeNumber);

  const [formData, setFormData] = useState({
    password: "",
    confirmPassword: "",
    studentFirstName: "",
    studentLastName: "",
    grade: "",
    school: "",
    gender: "",
    dateOfBirth: "",
    parentFirstName: "",
    parentLastName: "",
    parentEmail: "",
    parentPhone: ""
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Validate token on mount
  useEffect(() => {
    const validateToken = async () => {
      try {
        const response = await fetch(`/api/onboard/validate/${token}`);
        const data = await response.json();

        if (response.ok && data.valid) {
          setTokenValid(true);
          setEmail(data.email);
        } else {
          toast({
            title: "Invalid Link",
            description: "This onboarding link is invalid or has expired.",
            variant: "destructive"
          });
          setTimeout(() => setLocation("/"), 3000);
        }
      } catch (error) {
        console.error("Token validation error:", error);
        toast({
          title: "Error",
          description: "Failed to validate onboarding link.",
          variant: "destructive"
        });
      } finally {
        setValidating(false);
        setLoading(false);
      }
    };

    validateToken();
  }, [token, toast, setLocation]);

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 8) {
      newErrors.password = "Password must be at least 8 characters";
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    if (!formData.studentFirstName) {
      newErrors.studentFirstName = "Student first name is required";
    }

    if (!formData.studentLastName) {
      newErrors.studentLastName = "Student last name is required";
    }

    if (!formData.grade) {
      newErrors.grade = "Grade is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setSubmitting(true);

    try {
      const response = await fetch(`/api/onboard/${token}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok) {
        toast({
          title: "Welcome to EduFocal!",
          description: "Your account has been created successfully. You can now log in!",
        });

        // Redirect to login page
        setTimeout(() => {
          setLocation("/login");
        }, 2000);
      } else {
        toast({
          title: "Error",
          description: data.error || "Failed to complete onboarding",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error("Onboarding error:", error);
      toast({
        title: "Error",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive"
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (validating) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/5 via-chart-2/5 to-primary/10 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
            <p className="text-muted-foreground">Validating your onboarding link...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!tokenValid) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-chart-2/5 to-primary/10 flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl">
        <CardHeader>
          <CardTitle className="text-3xl text-center">Welcome to EduFocal!</CardTitle>
          <CardDescription className="text-center text-base">
            Complete your profile to get started on your learning journey
          </CardDescription>
          <div className="text-center mt-2">
            <p className="text-sm text-muted-foreground">Email: <span className="font-medium">{email}</span></p>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Password Section */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-primary" />
                Set Your Password
              </h3>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="password">Password *</Label>
                  <Input
                    id="password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => handleChange("password", e.target.value)}
                    data-testid="input-password"
                    className={errors.password ? "border-red-500" : ""}
                  />
                  {errors.password && <p className="text-sm text-red-500">{errors.password}</p>}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm Password *</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={formData.confirmPassword}
                    onChange={(e) => handleChange("confirmPassword", e.target.value)}
                    data-testid="input-confirm-password"
                    className={errors.confirmPassword ? "border-red-500" : ""}
                  />
                  {errors.confirmPassword && <p className="text-sm text-red-500">{errors.confirmPassword}</p>}
                </div>
              </div>
            </div>

            {/* Student Information */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-primary" />
                Student Information
              </h3>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="studentFirstName">First Name *</Label>
                  <Input
                    id="studentFirstName"
                    type="text"
                    value={formData.studentFirstName}
                    onChange={(e) => handleChange("studentFirstName", e.target.value)}
                    data-testid="input-first-name"
                    className={errors.studentFirstName ? "border-red-500" : ""}
                  />
                  {errors.studentFirstName && <p className="text-sm text-red-500">{errors.studentFirstName}</p>}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="studentLastName">Last Name *</Label>
                  <Input
                    id="studentLastName"
                    type="text"
                    value={formData.studentLastName}
                    onChange={(e) => handleChange("studentLastName", e.target.value)}
                    data-testid="input-last-name"
                    className={errors.studentLastName ? "border-red-500" : ""}
                  />
                  {errors.studentLastName && <p className="text-sm text-red-500">{errors.studentLastName}</p>}
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="grade">Grade *</Label>
                  <Select value={formData.grade} onValueChange={(value) => handleChange("grade", value)}>
                    <SelectTrigger id="grade" data-testid="select-grade" className={errors.grade ? "border-red-500" : ""}>
                      <SelectValue placeholder="Select grade" />
                    </SelectTrigger>
                    <SelectContent>
                      {activeGrades.map((g) => (
                        <SelectItem key={g.id} value={g.gradeNumber.toString()}>
                          {g.displayName}
                        </SelectItem>
                      ))}
                      {comingSoonGrades.map((g) => (
                        <SelectItem key={g.id} value={`coming-${g.gradeNumber}`} disabled className="text-muted-foreground opacity-50">
                          {g.displayName} (Coming Soon)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.grade && <p className="text-sm text-red-500">{errors.grade}</p>}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="school">School</Label>
                  <SchoolAutocomplete
                    id="school"
                    value={formData.school}
                    onChange={(v) => handleChange("school", v)}
                    placeholder="e.g. Campion College"
                    data-testid="input-school"
                  />
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="gender">Gender</Label>
                  <Select value={formData.gender} onValueChange={(value) => handleChange("gender", value)}>
                    <SelectTrigger id="gender" data-testid="select-gender">
                      <SelectValue placeholder="Select gender" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">Boy</SelectItem>
                      <SelectItem value="female">Girl</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="dateOfBirth">Date of Birth</Label>
                  <Input
                    id="dateOfBirth"
                    type="date"
                    value={formData.dateOfBirth}
                    onChange={(e) => handleChange("dateOfBirth", e.target.value)}
                    data-testid="input-dob"
                  />
                </div>
              </div>
            </div>

            {/* Parent/Guardian Information */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-primary" />
                Parent/Guardian Information
              </h3>
              <p className="text-sm text-muted-foreground">
                This helps us with prize delivery and important communications
              </p>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="parentFirstName">Parent First Name</Label>
                  <Input
                    id="parentFirstName"
                    type="text"
                    value={formData.parentFirstName}
                    onChange={(e) => handleChange("parentFirstName", e.target.value)}
                    data-testid="input-parent-first-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="parentLastName">Parent Last Name</Label>
                  <Input
                    id="parentLastName"
                    type="text"
                    value={formData.parentLastName}
                    onChange={(e) => handleChange("parentLastName", e.target.value)}
                    data-testid="input-parent-last-name"
                  />
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="parentEmail">Parent Email</Label>
                  <Input
                    id="parentEmail"
                    type="email"
                    value={formData.parentEmail}
                    onChange={(e) => handleChange("parentEmail", e.target.value)}
                    data-testid="input-parent-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="parentPhone">Parent Phone</Label>
                  <Input
                    id="parentPhone"
                    type="tel"
                    value={formData.parentPhone}
                    onChange={(e) => handleChange("parentPhone", e.target.value)}
                    placeholder="e.g. 876-xxx-xxxx"
                    data-testid="input-parent-phone"
                  />
                </div>
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full bg-gradient-to-r from-primary to-chart-2"
              disabled={submitting}
              data-testid="button-complete-onboarding"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating Your Account...
                </>
              ) : (
                "Complete Onboarding"
              )}
            </Button>

            <p className="text-xs text-center text-muted-foreground">
              * Required fields
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
